import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, Tag } from 'antd';
import InteractiveCompass from './InteractiveCompass';
import { ImageAnalysisResult } from '../types';
import {
  getPalaceLabel,
  getSittingFacingFromCompass,
  PalaceKey,
} from '../utils/houseLuckChart';

const getAirMouthOpeningCenter = (opening?: { bbox?: [number, number, number, number]; center: { x: number; y: number } }) => {
  if (!opening?.bbox) {
    return opening?.center;
  }

  const [x1, y1, x2, y2] = opening.bbox;
  return {
    x: (x1 + x2) / 2,
    y: (y1 + y2) / 2,
  };
};

interface CompassCalibrationProps {
  imageAnalysis: ImageAnalysisResult;
  previewUrl: string;
  rotation: number;
  onRotationChange: (rotation: number) => void;
  onSittingFacingChange: (value: {
    sitting: PalaceKey;
    facing: PalaceKey;
    sittingAngle: number;
    facingAngle: number;
  }) => void;
}

const CompassCalibration: React.FC<CompassCalibrationProps> = ({
  imageAnalysis,
  previewUrl,
  rotation,
  onRotationChange,
  onSittingFacingChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const visualizationImage = imageAnalysis.yoloAnalysis?.visualizationImage ?? previewUrl;
  const centerPoint = useMemo(() => (
    imageAnalysis.yoloAnalysis?.centerPoint ?? {
      x: imageAnalysis.width / 2,
      y: imageAnalysis.height / 2,
    }
  ), [imageAnalysis.height, imageAnalysis.width, imageAnalysis.yoloAnalysis?.centerPoint]);
  const entrance = useMemo(() => (
    imageAnalysis.yoloAnalysis?.entrance ?? {
      x: imageAnalysis.width / 2,
      y: imageAnalysis.height,
    }
  ), [imageAnalysis.height, imageAnalysis.width, imageAnalysis.yoloAnalysis?.entrance]);
  const airMouthCandidates = imageAnalysis.yoloAnalysis?.airMouthCandidates ?? [];
  const primaryAirMouth = airMouthCandidates[0];
  const candidateTarget = useMemo(() => {
    if (!primaryAirMouth || !imageAnalysis.yoloAnalysis) return entrance;

    const points = [
      ...imageAnalysis.yoloAnalysis.walls.flatMap(wall => [
        { x: wall.x1, y: wall.y1 },
        { x: wall.x2, y: wall.y2 },
      ]),
      ...imageAnalysis.yoloAnalysis.rooms.flatMap(room => [
        { x: room.bbox[0], y: room.bbox[1] },
        { x: room.bbox[2], y: room.bbox[3] },
      ]),
    ];
    if (points.length === 0) return entrance;

    const minX = Math.min(...points.map(point => point.x));
    const maxX = Math.max(...points.map(point => point.x));
    const minY = Math.min(...points.map(point => point.y));
    const maxY = Math.max(...points.map(point => point.y));

    if (primaryAirMouth.representativeOpening) {
      return getAirMouthOpeningCenter(primaryAirMouth.representativeOpening) ?? primaryAirMouth.representativeOpening.center;
    }

    if (primaryAirMouth.wall === 'north') return { x: (minX + maxX) / 2, y: minY };
    if (primaryAirMouth.wall === 'east') return { x: maxX, y: (minY + maxY) / 2 };
    if (primaryAirMouth.wall === 'south') return { x: (minX + maxX) / 2, y: maxY };
    return { x: minX, y: (minY + maxY) / 2 };
  }, [entrance, imageAnalysis.yoloAnalysis, primaryAirMouth]);
  const hasDetectedEntrance = Boolean(
    imageAnalysis.yoloAnalysis?.centerPoint && imageAnalysis.yoloAnalysis?.entrance,
  );

  const sittingFacing = useMemo(() => {
    return getSittingFacingFromCompass(centerPoint, candidateTarget, rotation);
  }, [candidateTarget, centerPoint, rotation]);

  useEffect(() => {
    onSittingFacingChange({
      sitting: sittingFacing.sitting,
      facing: sittingFacing.facing,
      sittingAngle: sittingFacing.sittingBearing,
      facingAngle: sittingFacing.facingBearing,
    });
  }, [
    onSittingFacingChange,
    sittingFacing.facing,
    sittingFacing.facingBearing,
    sittingFacing.sitting,
    sittingFacing.sittingBearing,
  ]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const canvasSize = 1024;
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const scale = Math.min(0.5, 620 / Math.max(img.width, img.height));
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const drawX = (canvasSize - drawWidth) / 2;
      const drawY = (canvasSize - drawHeight) / 2;

      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

      const toCanvas = (x: number, y: number) => ({
        x: drawX + x * scale,
        y: drawY + y * scale,
      });

      const center = toCanvas(centerPoint.x, centerPoint.y);
      const airMouth = toCanvas(candidateTarget.x, candidateTarget.y);

      ctx.setLineDash([8, 6]);
      ctx.strokeStyle = '#d3271f';
      ctx.lineWidth = 3;
      ctx.beginPath();
      if (primaryAirMouth?.wall === 'north' || primaryAirMouth?.wall === 'south') {
        ctx.moveTo(airMouth.x, drawY);
        ctx.lineTo(airMouth.x, drawY + drawHeight);
      } else if (primaryAirMouth?.wall === 'east' || primaryAirMouth?.wall === 'west') {
        ctx.moveTo(drawX, airMouth.y);
        ctx.lineTo(drawX + drawWidth, airMouth.y);
      } else {
        ctx.moveTo(center.x, center.y);
        ctx.lineTo(airMouth.x, airMouth.y);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = '#d3271f';
      ctx.beginPath();
      ctx.arc(center.x, center.y, 5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(airMouth.x, airMouth.y, 4, 0, 2 * Math.PI);
      ctx.fill();
    };
    img.src = visualizationImage;
  }, [candidateTarget, centerPoint, primaryAirMouth?.wall, visualizationImage]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  return (
    <Card className="result-card compass-calibration-card" title="校准罗盘" bordered={false}>
      <div className="compass-calibration-layout">
        <div className="compass-stage">
          <div className="compass-canvas-layer">
            <canvas ref={canvasRef} width={1024} height={1024} className="analysis-canvas" />
          </div>
          <div className="compass-overlay-layer">
            <InteractiveCompass initialRotation={rotation} onRotationChange={onRotationChange} />
          </div>
        </div>
        <div className="compass-calibration-copy">
          <span className="section-kicker">Compass calibration</span>
          <h3>按气口候选确定坐向</h3>
          <p>
            红色虚线优先指向评分最高的纳气候选面。现场先到该墙前读罗盘，若外局死窄或读数受干扰，再复核入户门。
            {!hasDetectedEntrance ? ' 未识别到入户门时，会退回按最强开窗面或户型边界估算。' : ''}
          </p>
          {primaryAirMouth && (
            <div className="air-mouth-primary">
              <div className="air-mouth-primary-head">
                <span>{primaryAirMouth.wallLabel}</span>
                <Tag color={primaryAirMouth.confidence === 'high' ? 'green' : primaryAirMouth.confidence === 'medium' ? 'gold' : 'default'}>
                  {primaryAirMouth.score}/{primaryAirMouth.maxScore}分
                </Tag>
              </div>
              <div className="air-mouth-breakdown">
                <span>开口 {primaryAirMouth.scoreBreakdown.openingQuality}</span>
                <span>开敞 {primaryAirMouth.scoreBreakdown.externalOpenness}</span>
                <span>动气 {primaryAirMouth.scoreBreakdown.movementQi}</span>
                <span>形法 {primaryAirMouth.scoreBreakdown.formBonus}</span>
              </div>
              {primaryAirMouth.externalConclusion && (
                <div className="air-mouth-external">
                  <strong>环境</strong>
                  <span>{primaryAirMouth.externalConclusion}</span>
                </div>
              )}
              <ul>
                {primaryAirMouth.evidence.slice(0, 3).map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {airMouthCandidates.length > 0 && (
            <div className="air-mouth-ranking">
              {airMouthCandidates.slice(0, 4).map(candidate => (
                <div key={candidate.id} className="air-mouth-row">
                  <span>{candidate.wallLabel}</span>
                  <strong>{candidate.score}</strong>
                </div>
              ))}
            </div>
          )}
          <div className="house-direction-note">
            坐{getPalaceLabel(sittingFacing.sitting)}
            {sittingFacing.sittingMountain}山 {Math.round(sittingFacing.sittingBearing)}°
            （{sittingFacing.sittingLineInfo.statusLabel}）
            · 向{getPalaceLabel(sittingFacing.facing)}
            {sittingFacing.facingMountain}向 {Math.round(sittingFacing.facingBearing)}°
            （{sittingFacing.facingLineInfo.statusLabel}）
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CompassCalibration;
